<template>
  <transition>
    <div v-if="showFailedExam" key="failed">
      <h3>Try again</h3>
      <p class="mb-5">
        You did not pass the exam and must retake the exam to continue. Please click the button below to try again.
      </p>
      <d-btn color="secondary-500" :loading="loading" @click="tryAgain">
        Retry
      </d-btn>
    </div>
    <div v-else key="exam-content">
      <div v-if="needPassword && (!examProgress || !examProgress.passed)" class="col-4">
        <h3>{{ contentItem.name }}</h3>
        <div class="pb-5 rich-text" v-html="contentItem.description" />
        <d-field label="Enter your exam password" rules="required">
          <d-input v-model="password" name="password" type="password" required style="width:200px" />
        </d-field>
        <d-btn color="success" @click="validateExamPassword">
          Start Exam
        </d-btn>
      </div>
      <div v-else-if="examProgress && examProgress.passed">
        <h3>{{ contentItem.name }}</h3>
        <div class="pb-5 rich-text" v-html="contentItem.description" />
        
        <!-- Exam Completion Display -->
        <div class="alert" :class="examProgress.passed ? 'alert-success' : 'alert-warning'">
          <h3>Exam Completed!</h3>
          <p>
            Score: {{ examProgress.questionsCorrect }} / {{ examProgress.questionsTotal }}
            ({{ Math.round((examProgress.questionsCorrect / examProgress.questionsTotal) * 100) }}%)
          </p>
          <p class="mt-2">
            <strong>Result: {{ examProgress.passed ? 'PASSED' : 'FAILED' }}</strong>
          </p>
        </div>
        
        <div v-if="examProgress.instructorFeedback" class="mt-3">
          <h4>You received the following feedback on your latest submission:</h4>
          <p>{{ examProgress.instructorFeedback }}</p>
        </div>
        
        <slot name="pagination" :passed="true" :check-if-passed="checkIfPassed" :submitting="submitting" />
      </div>
      <div v-else>
        <exam-taking-page 
          :exam-id="contentItem.id"
          :exam-name="contentItem.name"
          :exam-description="contentItem.description"
          :has-progress="!!examProgress"
          @exam-completed="handleExamCompleted"
        />
        <slot name="pagination" :passed="false" :check-if-passed="checkIfPassed" :submitting="submitting" />
      </div>
    </div>
  </transition>
</template>

<script>
  import { ref, computed, onMounted } from 'vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import ExamTakingPage from '../../../Resources/scripts/components/ExamTakingPage.vue';

  export default {
    components: {
      ExamTakingPage
    },
    props: {
      contentItem: {
        type: Object,
        default: () => ({}),
      },
    },

    setup(props) {
      const loading = ref(true);
      const submitting = ref(false);
      const showFailedExam = ref(false);
      const needPassword = ref(false);
      const examProgress = ref(null);
      const password = ref('');
      const toast = useToast();

      const passed = computed(() => {
        return examProgress.value && examProgress.value.passed;
      });

      const getExamProgress = async () => {
        try {
          const { data } = await axios.get(`/api/exams/${props.contentItem.id}/progress`);
          examProgress.value = data;
          
          // Check if user failed the exam - but only if exam is actually complete
          if (data && data.questionsCorrect !== undefined && data.questionsTotal !== undefined) {
            const answeredQuestions = (data.questionsCorrect || 0) + (data.questionsIncorrect || 0);
            const totalQuestions = data.questionsTotal || 0;
            
            // Only show failed state if all questions have been answered AND the exam was failed
            if (answeredQuestions >= totalQuestions) {
              const passThreshold = props.contentItem.passThreshold || 70; // Default pass threshold
              const score = (data.questionsCorrect / data.questionsTotal) * 100;
              if (score < passThreshold && !data.passed) {
                showFailedExam.value = true;
              }
            }
            // If not all questions answered, don't show failed state - let them continue
          }
        } catch (error) {
          console.error('Error loading exam progress:', error);
          // No progress found is OK - user hasn't started exam yet
          examProgress.value = null;
        }
      };

      const validateExamPassword = async () => {
        if (password.value === props.contentItem.password) {
          needPassword.value = false;
          toast.success('Exam password accepted! You can now start the exam.');
        } else {
          toast.error('Invalid exam password. Please try again.');
        }
      };

      const handleExamCompleted = async () => {
        // Refresh the exam progress to get the latest results
        // Add retry logic to handle cases where backend processing takes time
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          await getExamProgress();
          
          // If we have exam progress and it shows as passed, we're done
          if (examProgress.value && examProgress.value.passed) {
            break;
          }
          
          // If not passed yet, wait a bit and retry (backend might still be processing)
          if (retryCount < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
          retryCount++;
        }
      };

      const checkIfPassed = async () => {
        // Return the current pass status
        return examProgress.value && examProgress.value.passed;
      };

      const tryAgain = async () => {
        try {
          submitting.value = true;
          await axios.delete(`/api/exams/${props.contentItem.id}/progress`);
          examProgress.value = null;
          showFailedExam.value = false;
          submitting.value = false;
          toast.success('Exam reset successfully. You can now retake the exam.');
        } catch (error) {
          console.error('Error resetting exam:', error);
          toast.error('Failed to reset exam. Please try again.');
          submitting.value = false;
        }
      };

      onMounted(async () => {
        try {
          // Check if exam requires a password
          if (props.contentItem.password) {
            needPassword.value = true;
          }

          await getExamProgress();

          loading.value = false;
        } catch (error) {
          console.error('Error initializing exam module:', error);
          loading.value = false;
        }
      });

      return {
        loading,
        submitting,
        showFailedExam,
        needPassword,
        examProgress,
        password,
        passed,
        validateExamPassword,
        handleExamCompleted,
        checkIfPassed,
        tryAgain,
      };
    },
  };
</script>